import React, { useState, useCallback, useEffect } from 'react';
import NotFound from '../../common/404';
import { getUnitsDropDown } from '../../../utils/apicall/commonApi';
import { Unit, Users } from '../../../utils/types';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getUserSingle, updateUser, deleteUser } from '../../../utils/apicall/usersApi';
import { handleError, ApiError } from '../../../utils/error/error';
import { processError } from '../../../utils/error/errorUtils';
import GeneralErrorPage from '../../common/general-error';
import { authService } from '../../../utils/authServices';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { CONFIG } from '../../../utils/config';

const UserEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Users | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    accessible_units: [] as string[], // Store selected unit IDs here
  });
  const [password, setPassword] = useState<string>('');
  const [isValidId, setIsValidId] = useState(true);
  const navigate = useNavigate();
  const [units, setUnits] = useState<Unit[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const location = useLocation();


  useEffect(() => {
    if (!id || isNaN(Number(id))) {
      setIsValidId(false);
    }
  }, [id]);

  const loadData = useCallback(async () => {
    if (isValidId) {
      try {
        const response = await getUserSingle(parseInt(id!, 10));
        if (response) {
          setData(response);
          //setFormData(response);
          // Update formData based on response
          setFormData({
            name: response.name,
            email: response.email,
            password: '',
            role: response.roles[0]?.name || '', // Set role to the first role name
            accessible_units: response.accessible_units, // Map units to string IDs
          });
        }
      } catch (error) {
        console.error('An error occurred while fetching data.', error);
      }
    }
  }, [id, isValidId]);

  const loadUnits = useCallback(async () => {
    try {
      const response = await getUnitsDropDown();
      if (response) {
        setUnits(response);
      }
    } catch (error) {
      console.error('An error occurred while fetching units.', error);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadUnits();
    authService.getTokenExpiration();
  }, [loadData, loadUnits]);

  if (!isValidId) {
    return <NotFound />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
  
    // Handle checkbox specifically
    if (type === 'checkbox' && e.target instanceof HTMLInputElement) {
      const unitId = value; // Get the unit ID from the value
      const checked = e.target.checked; // Access the checked property
  
      setFormData((prevData) => {
        const accessibleUnitsArray = prevData.accessible_units ? [...prevData.accessible_units] : [];
        return {
          ...prevData,
          accessible_units: checked
            ? [...accessibleUnitsArray, unitId]
            : accessibleUnitsArray.filter((id) => id !== unitId),
        };
      });
    }else if (name === 'password') {
      setPassword(value);
    } else {
      // Handle other input types
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };
  



  const handleDelete = async () => {
          if (!id) return;
          setShowDeleteModal(false); // Close the modal after confirming

          const returnTo = location.state?.returnTo || "-1";
      try {
       const response = await deleteUser(parseInt(id, 10));
        
        setSuccessMessage('O χρήστης διαγράφηκε με επιτυχία!'); // Custom success message
            //setSuccessMessage(response.data.message); // Custom success message
        setTimeout(() => {
          // Go back to the previous page and pass a `refresh` flag
          navigate( CONFIG.urls.getUsersUrl_Admin(), { state: { refresh: true } });
        }, 800); // Adjust delay if necessary*/
        
      

      } catch (error) {
          handleError(error);
          processError(error, setErrors, setErrorMessage, setSuccessMessage);
      }
   };
 

   const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
 
    e.preventDefault();

    // Create finalData and dynamically add properties
    const finalData: any = {};
  
    // Add only non-empty fields to finalData
    if (formData.name) {
      finalData.name = formData.name;
    }
  
    if (formData.email) {
      finalData.email = formData.email;
    }
  
    if (password) {
      finalData.password = password; // Include only if not empty
    }
  
    if (formData.role) {
      finalData.role = formData.role;
    }
  
    if (formData.accessible_units && formData.accessible_units.length > 0) {
      let inputList = formData?.accessible_units;
     // console.log('formData?.accessible_units', typeof formData?.accessible_units);
     // console.log('formData?.accessible_units',formData?.accessible_units);
     // console.log('input',inputList);
      let processedList: any;
  
      if  (Array.isArray(inputList)){
         processedList =  inputList
            .filter((item: unknown) => typeof item === 'string' && item.trim().match(/^\d+$/)) // Ensure it's a numeric string
            .map((item: string) => Number(item.trim())) // Convert to number
        // Default to an empty array if `inputList` is not an array
  
      }else{
        let inputList: string | unknown =formData?.accessible_units;
        if (typeof inputList === "string") {
           processedList = inputList
            .replace(/[\[\]]/g, "") // Remove square brackets
            .split(",") // Split the string by commas
            .map((item: string) => {
              const num = Number(item.trim());
              if (isNaN(num)) throw new Error(`Invalid number: "${item}"`);
              return num;
            });
          }
       
    
      }
      finalData.accessible_units = processedList;
      console.log(finalData);
    }
 
    try {
      const returnTo =  "-1";
      const response = await updateUser(parseInt(id!, 10), finalData); // API call to update user
      setSuccessMessage("Αποθήκευση επιτυχής");
  
    
    } catch (error) {
      handleError(error);
      processError(error, setErrors, setErrorMessage, setSuccessMessage);
      if (error instanceof ApiError && error.statusCode === 404) {
        return <NotFound />;
      } else {
        console.error("An unexpected error occurred:", error);
        return <GeneralErrorPage />;
      }
    }
  };
  

   const accessibleUnitIds = formData?.accessible_units || [];
  return (
    <div className="content">
    <section className="ontotita py-5">
      <div className="container mt-4">
        <div className="card p-5">
        <h2 className="h5 mb-2 text-primary">Επεξεργασία χρήστη</h2>
      
        <hr />
          <form onSubmit={handleSubmit}>
          {successMessage && (
                            <div className="alert alert-success alert-dismissible fade show" role="alert">
                                <strong></strong> {successMessage}
                                <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="alert"
                                aria-label="Close"
                                ></button>
                            </div>
                            )}
                            {errorMessage && (
                            <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                <strong></strong> {errorMessage}
                                <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="alert"
                                aria-label="Close"
                                ></button>
                            </div>
                            )}
            <div className="mb-3">
              <label htmlFor="name" className="form-label">Ονοματεπώνυμο</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-person"></i></span>
                <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                value={formData?.name || ''}
                onChange={handleChange}
                />
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-envelope"></i></span>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  value={formData?.email || ''}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="password" className="form-label">Κωδικός</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-lock"></i></span>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  name="password"
                  value={password}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="role" className="form-label">Επιλογή Ρόλου</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-person-badge"></i></span>
                <select
                  className="form-select"
                  id="role"
                  name="role"
                  required
                  value={formData?.role}
                  onChange={handleChange}
                >
                  <option value="" disabled>Επιλέξτε ρόλο</option>
                  <option value="admin">Admin</option>
                  <option value="unitadmin">Unit Admin</option>
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Επιλογή Περιφερειακών Ενοτήτων</label>
              <div className="d-flex flex-wrap">
                {units.map((unit) => (
                  <div key={unit.id} className="form-check me-3">
                      <input
                       className="form-check-input"
                       type="checkbox"
                       id={`unit-${unit.id}`}
                       name="accessible_units"
                       value={unit.id?.toString()}
                       checked={accessibleUnitIds.includes(unit.id!.toString())}
                       onChange={handleChange}
                      />
                    <label className="form-check-label" htmlFor={`unit-${unit.id}`}>
                      {unit.title}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-end">
              <button type="submit" className="btn btn-primary">
                <i className="bi bi-save me-1"></i> Αποθήκευση
              </button>
        
              <button type="button" className="btn btn-danger ms-2"   onClick={() => setShowDeleteModal(true)}>
                                <i className="bi bi-trash me-1"></i> Διαγραφή
                            </button>
                            {/* Delete Confirmation Modal */}
                            <DeleteConfirmationModal
                                show={showDeleteModal}
                                onConfirm={handleDelete}
                                onCancel={() => setShowDeleteModal(false)} // Just close the modal
                                title="Επιβεβαίωση Διαγραφής"
                                message="Αυτή η ενέργεια θα διαγράψει οριστικά τον επιλεγμένο χρήστη. Είστε βέβαιοι;"
                                confirmText="Ναι, Διαγραφή"
                                cancelText="Ακύρωση"
                            />
                          </div>
         
          </form>
          </div>
          </div>
        </section>
      </div>
  );
};

export default UserEdit;
